import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminAPI } from '../services/api';

const AdminDashboardPage = () => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['admin-stats'],
    queryFn: async () => {
      const response = await adminAPI.getStats();
      return response.data;
    },
    refetchInterval: 30000, // Auto-refresh every 30 seconds
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            Admin Dashboard
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Overview of key metrics and system status
          </p>
        </div>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500 dark:text-gray-400 text-sm">
            Loading dashboard...
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            Admin Dashboard
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Overview of key metrics and system status
          </p>
        </div>
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <span className="text-red-400">⚠️</span>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800 dark:text-red-300">
                Error loading dashboard
              </h3>
              <div className="mt-2 text-sm text-red-700 dark:text-red-400">
                Failed to load dashboard data. Please try again.
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const stats = [
    {
      name: 'Total Users',
      value: data?.users?.total || 0,
      icon: '👥',
      color: 'bg-blue-500',
    },
    {
      name: 'Active Subscriptions',
      value: data?.billing?.activeSubscriptions || 0,
      icon: '💳',
      color: 'bg-emerald-500',
    },
    {
      name: 'New Users Today',
      value: data?.users?.newToday || 0,
      icon: '🆕',
      color: 'bg-green-500',
    },
    {
      name: 'Active Users Today',
      value: data?.users?.activeToday || 0,
      icon: '🟢',
      color: 'bg-purple-500',
    },
    {
      name: 'Total Projects',
      value: data?.projects?.total || 0,
      icon: '📁',
      color: 'bg-indigo-500',
    },
    {
      name: 'New Projects Today',
      value: data?.projects?.newToday || 0,
      icon: '✨',
      color: 'bg-pink-500',
    },
    {
      name: 'LLM Requests Today',
      value: data?.llmUsage?.requestsToday || 0,
      icon: '⚡',
      color: 'bg-orange-500',
    },
    {
      name: 'Okio Tokens Today',
      value: data?.llmUsage?.tokensChargedToday || 0,
      icon: '🪙',
      color: 'bg-yellow-500',
    },
    {
      name: 'Pending Matches',
      value: data?.objectives?.pendingMatches || 0,
      icon: '🎯',
      color: 'bg-red-500',
    },
  ];

  const totalUsers = data?.users?.total || 0;
  const activeSubs = data?.billing?.activeSubscriptions || 0;
  const activeSubsPct =
    typeof data?.billing?.activeSubscriptionsPct === 'number'
      ? data.billing.activeSubscriptionsPct
      : totalUsers
        ? activeSubs / totalUsers
        : 0;
  const activeSubsPctClamped = Math.max(0, Math.min(1, activeSubsPct));
  const activeSubsPctLabel = `${Math.round(activeSubsPctClamped * 100)}%`;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
          Admin Dashboard
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Overview of key metrics and system status
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => (
          <div
            key={stat.name}
            className="relative bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 pt-5 px-4 pb-0 sm:pt-6 sm:px-6 shadow rounded-lg overflow-hidden"
          >
            <dt>
              <div className={`absolute ${stat.color} rounded-md p-3`}>
                <span className="text-white text-xl">{stat.icon}</span>
              </div>
              <p className="ml-16 text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                {stat.name}
              </p>
            </dt>
            <dd className="ml-16 pb-6 flex items-baseline sm:pb-7">
              <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                {stat.value.toLocaleString()}
              </p>
            </dd>
          </div>
        ))}
      </div>

      {/* Detailed Sections */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Users Details */}
        <div className="rounded-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 p-4">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
            User Details
          </h2>
          <div className="space-y-2">
            <div className="flex items-baseline justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-300">
                Active
              </span>
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {data?.users?.active ?? '–'}
              </span>
            </div>
            <div className="flex items-baseline justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-300">
                Superusers
              </span>
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {data?.users?.superusers ?? '–'}
              </span>
            </div>
          </div>
        </div>

        {/* Billing Details */}
        <div className="rounded-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 p-4">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
            Billing
          </h2>
          <div className="space-y-2">
            <div className="flex items-baseline justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-300">
                Active subscriptions
              </span>
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {activeSubs.toLocaleString()}
                {totalUsers ? ` / ${totalUsers.toLocaleString()}` : ''}
              </span>
            </div>
            <div className="w-full">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Adoption
                </span>
                <span className="text-xs font-medium text-gray-700 dark:text-gray-200">
                  {activeSubsPctLabel}
                </span>
              </div>
              <div className="h-2 w-full rounded-full bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div
                  className="h-full bg-emerald-500"
                  style={{ width: activeSubsPctLabel }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Project Details */}
        <div className="rounded-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 p-4">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
            Project Details
          </h2>
          <div className="space-y-2">
            <div className="flex items-baseline justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-300">
                Personal
              </span>
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {data?.projects?.personal ?? '–'}
              </span>
            </div>
            <div className="flex items-baseline justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-300">
                Templates
              </span>
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {data?.projects?.templates ?? '–'}
              </span>
            </div>
            <div className="flex items-baseline justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-300">
                Active
              </span>
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {data?.projects?.active ?? '–'}
              </span>
            </div>
            <div className="flex items-baseline justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-300">
                Finished
              </span>
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {data?.projects?.finished ?? '–'}
              </span>
            </div>
            <div className="flex items-baseline justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-300">
                Public
              </span>
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {data?.projects?.public ?? '–'}
              </span>
            </div>
          </div>
        </div>

        {/* Engagement Details */}
        <div className="rounded-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 p-4">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
            Engagement
          </h2>
          <div className="space-y-2">
            <div className="flex items-baseline justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-300">
                Reviews
              </span>
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {data?.engagement?.reviewsCount ?? '–'}
              </span>
            </div>
            <div className="flex items-baseline justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-300">
                Average Rating
              </span>
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {data?.engagement?.averageRating
                  ? data.engagement.averageRating.toFixed(1)
                  : '–'}
              </span>
            </div>
            <div className="flex items-baseline justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-300">
                Messages Today
              </span>
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {data?.engagement?.messagesToday ?? '–'}
              </span>
            </div>
            <div className="flex items-baseline justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-300">
                Active Chats Today
              </span>
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {data?.engagement?.activeChatsToday ?? '–'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardPage;


